<template>
  <div class="loading" v-if="fetchData">
    <load-data></load-data>
  </div>
  <div v-else>
    <div class="card p-3">
      <div class="d-flex justify-content-between align-items-center my-2">
        <h2 class="font-weight-bolder">1. Staff Leave Cycle</h2>
        <button class="btn btn-primary" @click="openCreateLeaveCycle">Create</button>
      </div>
      <table class="table table-striped table-bordered">
        <thead class="thead-dark text-left">
          <th style="width:1%;white-space:nowrap">Sl No.</th>
          <th>Leave Cycle</th>
          <th>Start Date</th>
          <th>End Date</th>
          <th style="width:1%;white-space:nowrap">Action</th>
        </thead>
        <tbody>
          <tr v-for="(leaveCycle, index) in leaveCycles" :key="index">
            <td> {{ index+1 }} </td>
            <td> {{ leaveCycle.name }} </td>
            <td> {{ formatDate(leaveCycle.start_date) }} </td>
            <td> {{ formatDate(leaveCycle.end_date) }} </td>
            <td>
              <span @click="openEditLeaveCycle(leaveCycle)" class="text-primary" style="cursor:pointer;font-size:1.1rem;" title="Edit">
                <i class="far fa-edit"></i>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="card p-3 mt-2">
      <div class="d-flex justify-content-between align-items-center my-2">
        <h2><b>2. Staff Grouping for Assigning Leave Count</b></h2>
        <div class="d-flex align-items-center col-4">
          <label for="" class="w-100 my-0">Leave Cycle :</label>
          <select class="custom-select" v-model="selectedLeaveCycleId">
            <option :value="leaveCycle.id" v-for="(leaveCycle, index) in leaveCycles" :key="index">{{ leaveCycle.name }}</option>
          </select>
        </div>
        <button class="btn btn-primary mt-1" @click="openCreateGroup">Create Group</button>
      </div>
      <table class="table table-striped table-bordered">
        <thead class="thead-dark text-left">
          <th style="width:1%;white-space:nowrap">Sl No.</th>
          <th>Group Name</th>
          <th>Staff List</th>
          <th>Leave Types</th>
          <th style="white-space:nowrap">Approval</th>
          <th style="width:1%;white-space:nowrap">Action</th>
        </thead>
        <tbody>
          <tr v-if="!leaveGroups || leaveGroups.length === 0">
            <td colspan="6" class="text-center text-muted">No group created</td>
          </tr>
          <tr v-for="(leaveGroup, index) in leaveGroups" :key="index">
            <td> {{ index+1 }} </td>
            <td> {{ leaveGroup.name }} </td>
            <td>
              <b-link @click="openGroupFaculties(leaveGroup)">{{ (leaveGroup.faculties || []).length }} Added</b-link>
            </td>
            <td>
              {{ (leaveGroup.leave_types || []).map(l => (l.leave_type.name+'-'+(l.is_unlimited ? 'NA' : l.max_leaves))).join(', ') }}
            </td>
            <td>
              <!-- 1: straight to the approver, 2: HOD recommends first -->
              {{ leaveGroup.approval_level == 2 ? '2 level (HOD recommendation)' : 'Single level' }}
            </td>
            <td style="white-space:nowrap">
              <span
                @click="openEditGroup(leaveGroup)"
                class="text-primary mr-2"
                style="cursor:pointer;font-size:1.1rem;"
                title="Edit"
              >
                <i class="far fa-edit"></i>
              </span>
              <b-link @click="openLeaveGroupLog(leaveGroup)">Log</b-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="card p-3 mt-2">
      <div class="d-flex justify-content-between align-items-center my-2">
        <h2 class="font-weight-bolder mb-0">3. <span class="text-primary" style="cursor:pointer;text-decoration:underline;" @click="openStaffLeaveStartingCount">Staff Leave Starting Count</span></h2>
      </div>
      <p class="text-muted mb-0">Enter the number of leaves each staff member had already taken before using the Staff Leave module in EzyGo.</p>
    </div>

    <!-- Staff Leave Starting Count: leaves already taken before this module, one row per staff mapped to the leave cycle, saved row by row -->
    <b-modal id="staffLeaveStartingCountModal" title="Staff Leave Starting Count" dialog-class="starting-count-fullscreen" hide-footer>
      <div class="card p-3 starting-count-filter">
        <div class="d-flex align-items-center starting-count-cycle">
          <label class="mr-2 my-0" style="white-space:nowrap;">Leave Cycle :</label>
          <select class="custom-select" v-model="startingCountLeaveCycleId" @change="loadStartingCounts">
            <option :value="leaveCycle.id" v-for="(leaveCycle, index) in leaveCycles" :key="index">{{ leaveCycle.name }}</option>
          </select>
        </div>
      </div>
      <div v-if="loadingStartingCounts" class="text-center my-3">
        <b-spinner small></b-spinner>
      </div>
      <div v-else class="mt-3 starting-count-table">
        <table class="table table-sm table-striped table-bordered mb-2">
          <thead class="thead-dark text-left">
            <tr>
              <th style="width:1%;white-space:nowrap;vertical-align:middle">Sl No.</th>
              <th style="min-width:200px;vertical-align:middle">Staff Name</th>
              <th style="min-width:120px;vertical-align:middle">Group Name</th>
              <th v-for="leaveType in startingCountLeaveTypes" :key="'lt' + leaveType.id" class="leave-type-head">
                {{ leaveType.name }}
              </th>
              <th style="width:1%;white-space:nowrap;vertical-align:middle">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="startingCountRows.length === 0">
              <td :colspan="startingCountLeaveTypes.length + 4" class="text-center text-muted">No staff mapped to this leave cycle</td>
            </tr>
            <tr v-for="(row, index) in startingCountRows" :key="row.faculty.id">
              <td class="align-middle"> {{ index+1 }} </td>
              <td class="align-middle"> {{ row.faculty.name }} </td>
              <td class="align-middle"> {{ row.group.name }} </td>
              <td class="align-middle" v-for="leaveType in startingCountLeaveTypes" :key="'c' + leaveType.id">
                <!-- only the leave types of the staff's group can be entered -->
                <input
                  v-if="row.leaveTypeIds.includes(leaveType.id)"
                  type="number"
                  min="0"
                  class="form-control form-control-sm"
                  v-model="row.counts[leaveType.id]"
                  :disabled="!row.editing"
                />
                <span v-else class="d-block text-center text-muted">-</span>
              </td>
              <td class="text-center align-middle" style="white-space:nowrap">
                <template v-if="!row.editing">
                  <span @click="editStartingCountRow(row)" class="text-primary mr-2" style="cursor:pointer;" title="Edit">
                    <i class="far fa-edit" style="font-size: 15px !important;"></i>
                  </span>
                  <b-link v-if="row.saved" @click="openStartingCountLog(row)">Log</b-link>
                </template>
                <template v-else>
                  <button
                    class="btn btn-sm py-0 mr-1"
                    :class="row.saved ? 'btn-primary' : 'btn-success'"
                    @click="saveStartingCountRow(row)"
                    :disabled="row.saving"
                  >
                    {{ row.saving ? 'Saving...' : (row.saved ? 'Update' : 'Save') }}
                  </button>
                  <button class="btn btn-danger btn-sm py-0" @click="cancelStartingCountRow(row)" :disabled="row.saving">Cancel</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <!-- log of one faculty group: created, edits and faculty added / removed -->
    <b-modal id="leaveGroupLogModal" :title="'Staff Group - Log' + (logGroupName ? ' (' + logGroupName + ')' : '')" size="xl" hide-footer>
      <div v-if="loadingLeaveGroupLog" class="text-center my-3">
        <b-spinner small></b-spinner>
      </div>
      <div v-else style="max-height:60vh;overflow-y:auto;">
        <table class="table table-sm table-striped table-bordered">
          <thead class="thead-dark text-left">
            <th style="width:1%;white-space:nowrap">Sl No.</th>
            <th style="white-space:nowrap">Action</th>
            <th>Details</th>
            <th>Updated By</th>
            <th>Updated At</th>
          </thead>
          <tbody>
            <tr v-if="leaveGroupLogs.length === 0">
              <td colspan="5" class="text-center text-muted">No changes logged</td>
            </tr>
            <tr v-for="(log, index) in leaveGroupLogs" :key="log.id">
              <td> {{ index+1 }} </td>
              <td style="white-space:nowrap"> {{ logActionTitle(log.action) }} </td>
              <td>
                <div v-if="log.action === 'created'" class="font-weight-bold mb-1">Initial Data</div>
                <ul class="mb-0 pl-3">
                  <li v-for="(line, i) in log.details" :key="i">{{ line }}</li>
                </ul>
              </td>
              <td> {{ log.updated_by }} </td>
              <td style="white-space:nowrap"> {{ formatDateTime(log.updated_at) }} </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <!-- log of one staff row of the starting count popup, each save with all its changes together -->
    <b-modal id="staffLeaveStartingCountLogModal" :title="'Staff Leave Starting Count - Log' + (logStaffName ? ' (' + logStaffName + ')' : '')" size="xl" hide-footer>
      <div v-if="loadingStartingCountLog" class="text-center my-3">
        <b-spinner small></b-spinner>
      </div>
      <div v-else style="max-height:60vh;overflow-y:auto;">
        <table class="table table-sm table-striped table-bordered">
          <thead class="thead-dark text-left">
            <th style="width:1%;white-space:nowrap">Sl No.</th>
            <th style="white-space:nowrap">Action</th>
            <th>Details</th>
            <th>Updated By</th>
            <th>Updated At</th>
          </thead>
          <tbody>
            <tr v-if="startingCountLogs.length === 0">
              <td colspan="5" class="text-center text-muted">No changes logged</td>
            </tr>
            <tr v-for="(log, index) in startingCountLogs" :key="log.id">
              <td> {{ index+1 }} </td>
              <td style="white-space:nowrap"> {{ logActionTitle(log.action) }} </td>
              <td>
                <div v-if="log.action === 'created'" class="font-weight-bold mb-1">Initial Data</div>
                <ul class="mb-0 pl-3">
                  <li v-for="(line, i) in log.details" :key="i">{{ line }}</li>
                </ul>
              </td>
              <td> {{ log.updated_by }} </td>
              <td style="white-space:nowrap"> {{ formatDateTime(log.updated_at) }} </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>

    <div class="card p-3 mt-2">
      <div class="d-flex justify-content-between align-items-center my-2">
        <h2 class="font-weight-bolder">4. Staff Leave Approver</h2>
      </div>
      <p class="text-muted">The selected user will see the Staff Leave Requests tile on the admin dashboard.</p>
      <div class="d-flex align-items-center">
        <label class="mr-2 my-0" style="white-space:nowrap;">Select User :</label>
        <select class="custom-select mr-2" v-model="leaveViewerUserId" :disabled="loadingFaculties">
          <option :value="null">-- None --</option>
          <option :value="faculty.id" v-for="faculty in institutionFaculties" :key="faculty.id">
            {{ faculty.name }} ({{ faculty.email }})
          </option>
        </select>
        <button class="btn btn-primary mr-2" @click="saveLeaveViewerSetting" :disabled="savingViewer">
          {{ savingViewer ? 'Saving...' : 'Save' }}
        </button>
        <button class="btn btn-outline-secondary" @click="clearLeaveViewerSetting" :disabled="savingViewer">
          Clear
        </button>
      </div>
      <b-form-checkbox class="mt-3" v-model="whatsAppAlert" :disabled="savingWhatsAppAlert" @change="saveWhatsAppAlertSetting">
        The approver will receive a WhatsApp notification when a staff member submits a leave request.
      </b-form-checkbox>
      <!-- always on, the HOD recommends every staff leave request before the approver; hidden for now -->
      <!-- <b-form-checkbox class="mt-2" :checked="true" disabled>
        Leave requests must first be recommended by the staff member's HOD before they reach the approver.
      </b-form-checkbox> -->
    </div>

    <create-leave-cycle :leave-cycle="editingLeaveCycle" @created="getLeaveCycles" @updated="getLeaveCycles"></create-leave-cycle>
    <create-leave-group :leave-cycle-id="selectedLeaveCycleId" :group="editingGroup" @created="getLeaveGroups" @updated="getLeaveGroups"></create-leave-group>
    <group-faculties :faculties="selectedGroupFaculties" :group="selectedGroup" :leave-groups="leaveGroups" @assigned="onFacultyAssigned"></group-faculties>
    <group-leave-types :leave-types="selectedGroupLeaveTypes" :group="selectedGroup" @updated="getLeaveGroups"></group-leave-types>
  </div>
</template>

<script>
import axios from "axios";
import CreateLeaveCycle from "./facultyLeaveComponents/CreateLeaveCycle.vue";
import CreateLeaveGroup from "./facultyLeaveComponents/CreateLeaveGroup.vue";
import GroupFaculties from "./facultyLeaveComponents/GroupFaculties.vue";
import GroupLeaveTypes from "./facultyLeaveComponents/GroupLeaveTypes.vue";

export default {
  components: { CreateLeaveCycle, CreateLeaveGroup, GroupFaculties, GroupLeaveTypes },
  async created() {
    await this.getLeaveCycles();
    this.getLeaveViewerSetting();
    this.getWhatsAppAlertSetting();    this.getInstitutionFaculties();
  },

  watch: {
    selectedLeaveCycleId: {
      immediate: true,
      handler() {
        this.getLeaveGroups();
      },
    },
  },

  data() {
    return {
      fetchData: false,
      leaveCycles: [],
      editingLeaveCycle: null,
      leaveGroups: [],
      selectedLeaveCycleId: null,
      selectedGroup: {},
      editingGroup: {},
      selectedGroupFaculties: [],
      selectedGroupLeaveTypes: [],
      leaveViewerUserId: null,
      institutionFaculties: [],
      loadingFaculties: false,
      savingViewer: false,
      whatsAppAlert: true,
      savingWhatsAppAlert: false,      startingCountLeaveCycleId: null,
      startingCountRows: [],
      startingCountLeaveTypes: [],
      loadingStartingCounts: false,
      logStaffName: "",
      startingCountLogs: [],
      loadingStartingCountLog: false,
      leaveGroupLogs: [],
      loadingLeaveGroupLog: false,
      logGroupName: "",
    };
  },

  methods: {

    formatDate(date) {
      if (!date) return "";
      const [y, m, d] = date.split("-");
      return `${d}-${m}-${y}`;
    },

    openCreateLeaveCycle() {
      this.editingLeaveCycle = null;
      this.$nextTick(() => this.$bvModal.show("createLeaveCycleModal"));
    },

    openEditLeaveCycle(leaveCycle) {
      this.editingLeaveCycle = leaveCycle;
      this.$nextTick(() => this.$bvModal.show("createLeaveCycleModal"));
    },

    // opens on the leave cycle selected in section 2
    openStaffLeaveStartingCount() {
      this.startingCountLeaveCycleId = this.selectedLeaveCycleId;
      this.loadStartingCounts();
      this.$bvModal.show("staffLeaveStartingCountModal");
    },

    // log time as dd-mm-yyyy hh:mm AM/PM
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      const pad = (n) => String(n).padStart(2, "0");
      let hours = date.getHours();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(hours)}:${pad(date.getMinutes())} ${ampm}`;
    },

    // log of one staff row
    async openStartingCountLog(row) {
      this.startingCountLogs = [];
      this.logStaffName = row.faculty.name;
      this.$bvModal.show("staffLeaveStartingCountLogModal");

      this.loadingStartingCountLog = true;
      const url = this.$store.getters.getAPIKey.mainAPI
        + `/faculty-leave/starting-count/log?leave_cycle_id=${this.startingCountLeaveCycleId}&institution_user_id=${row.faculty.id}`;
      await axios.get(url)
        .then((response) => {
          this.startingCountLogs = response.data.data || [];
        })
        .catch(() => {
          this.$toast.error("Failed to load log", { position: "top", duration: 3000 });
        });
      this.loadingStartingCountLog = false;
    },

    // action column text for both log popups
    logActionTitle(action) {
      return {
        created: "Created",
        updated: "Updated",
        deleted: "Deleted",
        "faculty added": "Staff Added",
        "faculty removed": "Staff Removed",
      }[action] || action;
    },

    async openLeaveGroupLog(leaveGroup) {
      this.leaveGroupLogs = [];
      this.logGroupName = leaveGroup.name;
      this.$bvModal.show("leaveGroupLogModal");

      this.loadingLeaveGroupLog = true;
      const url = this.$store.getters.getAPIKey.mainAPI + `/faculty-leave/group/${leaveGroup.id}/log`;
      await axios.get(url)
        .then((response) => {
          this.leaveGroupLogs = response.data.data || [];
        })
        .catch(() => {
          this.$toast.error("Failed to load log", { position: "top", duration: 3000 });
        });
      this.loadingLeaveGroupLog = false;
    },

    // keep a copy so Cancel can put the row back
    editStartingCountRow(row) {
      row.backup = { ...row.counts };
      row.editing = true;
    },

    cancelStartingCountRow(row) {
      row.counts = row.backup;
      row.editing = false;
    },

    // saves only this row; the backend replaces the staff's counts for the leave cycle
    async saveStartingCountRow(row) {
      const counts = [];
      row.leaveTypeIds.forEach((leaveTypeId) => {
        const value = row.counts[leaveTypeId];
        if (value === undefined || value === null || value === "") return;
        counts.push({
          leave_group_id: row.group.id,
          leave_type_id: leaveTypeId,
          starting_count: Number(value),
        });
      });

      // a saved row can be cleared, which removes its counts
      if (!counts.length && !row.saved) {
        this.$toast.error("Enter at least one leave count", { position: "top", duration: 3000 });
        return;
      }

      row.saving = true;
      const url = this.$store.getters.getAPIKey.mainAPI + `/faculty-leave/starting-count`;
      await axios.post(url, {
        leave_cycle_id: this.startingCountLeaveCycleId,
        institution_user_id: row.faculty.id,
        original_institution_user_id: row.saved ? row.faculty.id : null,
        counts,
      })
        .then(() => {
          this.$toast.success("Leave starting count saved", { position: "top", duration: 3000 });
          row.saved = counts.length > 0;
          row.editing = false;
        })
        .catch((error) => {
          const message = (error.response && error.response.data && error.response.data.message) || "Failed to save leave starting count";
          this.$toast.error(message, { position: "top", duration: 3000 });
        })
        .finally(() => {
          row.saving = false;
        });
    },

    // every staff mapped to a group of the selected leave cycle becomes a row (alphabetical),
    // the leave types of all groups become the columns, saved counts fill the rows
    async loadStartingCounts() {
      this.startingCountLeaveTypes = [];
      this.startingCountRows = [];
      if (!this.startingCountLeaveCycleId) return;

      this.loadingStartingCounts = true;
      const mainAPI = this.$store.getters.getAPIKey.mainAPI;
      const cycleId = this.startingCountLeaveCycleId;

      await Promise.all([
        axios.get(mainAPI + `/faculty-leave/group?leave_cycle_id=${cycleId}`),
        axios.get(mainAPI + `/faculty-leave/starting-count?leave_cycle_id=${cycleId}`),
      ])
        .then(([groupResponse, countResponse]) => {
          const groups = groupResponse.data.data || [];

          const leaveTypes = {};
          groups.forEach((group) => {
            (group.leave_types || []).forEach((lt) => {
              leaveTypes[lt.leave_type_id] = { id: lt.leave_type_id, name: lt.leave_type.name };
            });
          });
          this.startingCountLeaveTypes = Object.values(leaveTypes)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

          const savedByStaff = {};
          (countResponse.data.data || []).forEach((saved) => {
            savedByStaff[saved.institution_user_id] = saved.counts;
          });

          const rows = [];
          groups.forEach((group) => {
            const leaveTypeIds = (group.leave_types || []).map((lt) => lt.leave_type_id);
            (group.faculties || []).forEach((f) => {
              const user = f.institution_user;
              if (!user) return;
              const saved = savedByStaff[user.id] || [];
              const counts = {};
              saved.forEach((c) => {
                if (leaveTypeIds.includes(c.leave_type_id)) counts[c.leave_type_id] = Number(c.starting_count);
              });
              rows.push({
                faculty: { id: user.id, name: [user.first_name, user.last_name].filter(Boolean).join(' ') },
                group: { id: group.id, name: group.name },
                leaveTypeIds,
                counts,
                editing: false,
                saving: false,
                saved: saved.length > 0,
                backup: null,
              });
            });
          });

          this.startingCountRows = rows.sort((a, b) => a.faculty.name.localeCompare(b.faculty.name, undefined, { sensitivity: 'base' }));
        })
        .catch(() => {
          this.$toast.error("Failed to load leave starting counts", { position: "top", duration: 3000 });
        });

      this.loadingStartingCounts = false;
    },

    openCreateGroup() {
      this.editingGroup = null;
      this.$nextTick(() => this.$bvModal.show("createLeaveGroupModal"));
    },

    openEditGroup(leaveGroup) {
      this.editingGroup = leaveGroup;
      this.$nextTick(() => this.$bvModal.show("createLeaveGroupModal"));
    },

    openGroupFaculties(leaveGroup) {
      this.selectedGroup = leaveGroup;
      this.selectedGroupFaculties = leaveGroup.faculties || [];
      this.$bvModal.show("groupFacultiesModal");
    },

    openGroupLeaveTypes(leaveGroup) {
      this.selectedGroup = leaveGroup;
      this.selectedGroupLeaveTypes = leaveGroup.leave_types || [];
      this.$bvModal.show("groupLeaveTypesModal");
    },

    async onFacultyAssigned() {
      await this.getLeaveGroups();
      const refreshed = this.leaveGroups.find((g) => g.id === this.selectedGroup.id);
      if (refreshed) {
        this.selectedGroup = refreshed;
        this.selectedGroupFaculties = refreshed.faculties || [];
      }
    },

    async getLeaveCycles() {
      this.fetchData = true;

      const url = this.$store.getters.getAPIKey.mainAPI + `/faculty-leave`;

      await axios.get(url)
        .then((response) => {
          this.leaveCycles = response.data.data.leave_cycles || [];
          if (this.leaveCycles.length) this.selectedLeaveCycleId = response.data.data.currentLeaveCycleId;
        })
        .catch(() => {
          this.$toast.error("Failed to load staff leave settings", {
            position: "top",
            duration: 3000,
          });
        });
      
      this.fetchData = false;
    },

    async getInstitutionFaculties() {
      this.loadingFaculties = true;
      const url = this.$store.getters.getAPIKey.mainAPI + `/institutionusers/admins`;
      await axios.get(url)
        .then((response) => {
          this.institutionFaculties = response.data.map((f) => ({
            id: f.id,
            name: [f.first_name, f.last_name].filter(Boolean).join(' '),
            email: f.email,
          })).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        })
        .catch(() => {
          this.$toast.error("Failed to load staff list", { position: "top", duration: 3000 });
        });
      this.loadingFaculties = false;
    },

    async getLeaveViewerSetting() {
      const url = this.$store.getters.getAPIKey.institutionSettings.replace("setting_key", "faculty_leave_requests_viewer_id");
      await axios.get(url)
        .then((response) => {
          this.leaveViewerUserId = response.data ? Number(response.data) : null;
        })
        .catch(() => {});
    },

    async saveLeaveViewerSetting() {
      this.savingViewer = true;
      const url = this.$store.getters.getAPIKey.institutionSettings.replace("setting_key", "faculty_leave_requests_viewer_id");
      await axios.post(url, { faculty_leave_requests_viewer_id: this.leaveViewerUserId })
        .then(() => {
          this.$toast.success("Approver setting saved", { position: "top", duration: 3000 });
        })
        .catch(() => {
          this.$toast.error("Failed to save approver setting", { position: "top", duration: 3000 });
        });
      this.savingViewer = false;
    },

    async getWhatsAppAlertSetting() {
      const url = this.$store.getters.getAPIKey.institutionSettings.replace("setting_key", "faculty_leave_whatsapp_alert");
      await axios.get(url)
        .then((response) => {
          // enabled by default, only an explicitly saved "disabled" turns it off
          this.whatsAppAlert = response.data !== "disabled";
        })
        .catch(() => {});
    },

    async saveWhatsAppAlertSetting(value) {
      this.savingWhatsAppAlert = true;
      const url = this.$store.getters.getAPIKey.institutionSettings.replace("setting_key", "faculty_leave_whatsapp_alert");
      await axios.post(url, { faculty_leave_whatsapp_alert: value ? "enabled" : "disabled" })
        .then(() => {
          this.$toast.success(value ? "WhatsApp notification enabled" : "WhatsApp notification disabled", { position: "top", duration: 3000 });
        })
        .catch(() => {
          this.whatsAppAlert = !value;
          this.$toast.error("Failed to save WhatsApp notification setting", { position: "top", duration: 3000 });
        });
      this.savingWhatsAppAlert = false;
    },

    async clearLeaveViewerSetting() {
      this.leaveViewerUserId = null;
      await this.saveLeaveViewerSetting();
    },

    async getLeaveGroups() {
      if (!this.selectedLeaveCycleId) return;

      const url = this.$store.getters.getAPIKey.mainAPI + `/faculty-leave/group?leave_cycle_id=${this.selectedLeaveCycleId}`;

      await axios.get(url)
        .then((response) => {
          this.leaveGroups = response.data.data || [];
        })
        .catch(() => {
          this.$toast.error("Failed to load staff leave settings", {
            position: "top",
            duration: 3000,
          });
        });
      
      this.fetchData = false;
    },


  }
};
</script>

<style scoped>
/* keep the filter card its natural height in the modal's flex column */
.starting-count-filter {
  flex: 0 0 auto;
  background-color: #eef4fb;
  border-color: #d6e4f5;
}

.starting-count-cycle {
  width: 41.667%;
}

/* the table scrolls inside the modal so its header row stays fixed at the top */
.starting-count-table {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.starting-count-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.leave-type-head {
  min-width: 75px;
  white-space: normal;
  font-size: 12px;
  text-align: center;
  vertical-align: middle;
}

.subtitle {
  font-size: 16px;
  font-weight: bold;
  display: block;
  margin-bottom: 10px;
}

.radio-group {
  background: #ffffff;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
}

.radio-label {
  display: block;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
}

.checkbox-group {
  background: #ffffff;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
}

.checkbox-label {
  font-size: 14px;
  font-weight: bold;
}

.status {
  font-size: 14px;
  font-weight: bold;
  margin-left: 20px;
}

.approved {
  color: green;
}

.recommended {
  color: blue;
}

.forwarded {
  color: purple;
}
</style>

<style>
/* the modal is moved to <body>, so this can't be scoped */
.starting-count-fullscreen {
  max-width: 100%;
  width: 100%;
  height: 100%;
  margin: 0;
}

.starting-count-fullscreen .modal-content {
  height: 100%;
  border-radius: 0;
}

.starting-count-fullscreen .modal-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
</style>
